import type { TFunction } from "i18next"
import { safeStorage } from "../../../../helpers/safeStorage"

export const DISCOUNT_CODE_INVALID = "DISCOUNT_CODE_INVALID"

/** Gründe aus `extensions.reason` des Servers (DiscountCodeRejectReason). */
export type DiscountCodeRejectReason = "Unknown" | "Deactivated" | "NotYetValid" | "Expired" | "WrongCourse" | "Exhausted"

export const discountCodeRejectText = (reason: string | undefined, t: TFunction): string => {
    switch (reason as DiscountCodeRejectReason | undefined) {
        case "NotYetValid":
            return t("discountCode.rejected.NotYetValid", "Dieser Code ist noch nicht gültig.")
        case "Expired":
            return t("discountCode.rejected.Expired", "Dieser Code ist abgelaufen.")
        case "WrongCourse":
            return t("discountCode.rejected.WrongCourse", "Dieser Code gilt nicht für diesen Kurs.")
        case "Exhausted":
            return t("discountCode.rejected.Exhausted", "Dieser Code wurde bereits so oft eingelöst wie möglich.")
        case "Deactivated":
            return t("discountCode.rejected.Deactivated", "Dieser Code ist nicht mehr gültig.")
        default:
            return t("discountCode.rejected.Unknown", "Diesen Code kennen wir nicht. Bitte prüfe die Schreibweise.")
    }
}

/** Liest Code und Grund aus einem Apollo-Fehler, falls es eine Rabattcode-Ablehnung ist. */
export const getDiscountCodeRejection = (error: unknown): { reason?: string } | null => {
    const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string; reason?: string } }> } | null)?.graphQLErrors ?? []
    const rejection = graphQLErrors.find((graphQLError) => graphQLError.extensions?.code === DISCOUNT_CODE_INVALID)

    return rejection ? { reason: rejection.extensions?.reason } : null
}

/** URL-Parameter aus dem Eltern-Link: `+` ist dort ein Leerzeichen (URLSearchParams), `%20` ebenso. */
export const decodeDiscountCodeParam = (raw: string): string => {
    try {
        return decodeURIComponent(raw.replace(/\+/g, " "))
    } catch {
        return raw
    }
}

/** Wie der Server: ohne Leerzeichen, Großbuchstaben (Handy-Tastatur, „bischberg 15“). */
export const normalizeDiscountCodeInput = (raw: string): string => raw.replace(/\s+/g, "").toUpperCase()

// Der eingelöste Rabatt wandert über den Speicher zur Ergebnisseite („Rabattcode X eingelöst, du sparst Y“):
// die Ergebnisseite kennt die Buchung nicht, und der Wert ist nur eine Anzeige.
const APPLIED_DISCOUNT_KEY = "appliedDiscount"

/** `currency` als Kurzform („EUR“, „CHF“) der Mandanten-Währung, damit die Ergebnisseite nicht auf EUR zurückfällt. */
export type AppliedDiscount = { code: string; amount: number; currency?: string }

export const rememberAppliedDiscount = (applied: AppliedDiscount | null): void => {
    if (applied) {
        safeStorage.setItem(APPLIED_DISCOUNT_KEY, JSON.stringify(applied))
    } else {
        safeStorage.removeItem(APPLIED_DISCOUNT_KEY)
    }
}

export const takeAppliedDiscount = (): AppliedDiscount | null => {
    const raw = safeStorage.getItem(APPLIED_DISCOUNT_KEY)

    safeStorage.removeItem(APPLIED_DISCOUNT_KEY)
    if (!raw) {
        return null
    }
    try {
        const parsed = JSON.parse(raw) as Partial<AppliedDiscount>

        return parsed.code && typeof parsed.amount === "number" ? { code: parsed.code, amount: parsed.amount, currency: parsed.currency } : null
    } catch {
        return null
    }
}
